import * as _ from 'lodash'
import {IBusinessObject, IBusinessObjectCreateLoadOptions} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, IIdentityProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_EntityRow,
    WB_MasterDataSetRow,
    WB_ProductConditionTypeRow,
    WB_SourceRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbProductConditionType} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Entity,
    esWB_MasterData_Set,
    esWB_Product_Condition_Type, esWB_Source
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ASync} from "../PA.Core/Basic/ASync";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import { ProductConditionTypeBOValidations } from './ProductConditionTypeBO.Validation';
import {AuditService} from "../PA.Core/Security/AuditService";
import {ProductConditionTypeBOAudit} from "./ProductConditionTypeBO.Audit";
import {ExecutionSerializer} from "../PA.Core/Basic/ExecutionSerializer";

export class ProductConditionTypeBO 
    implements 
        IBusinessObject
{
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_ProductConditionType: WB_ProductConditionTypeRow;

    constructor(options?: IBusinessObjectCreateLoadOptions) {

        this.CreateOptions = options || {};

        if('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New ProductConditionType'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_ProductConditionType = undefined;
    }

    get WB_ProductConditionType(): WB_ProductConditionTypeRow
    {
        return this.drWB_ProductConditionType;
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: IBusinessObjectCreateLoadOptions): Promise<IBusinessObject> {
        const self = this;

        this.LoadProgressProvider.start("Creating ProductConditionType");
        let internalOnComplete = self._OnLoadComplete = (async () => {
            try {
                self.LoadOptions = options || {};
                if ('dbContext' in self.LoadOptions)
                    self.dbContext = self.LoadOptions.dbContext;
                else
                    self.dbContext = new PAdbContext();

                if (this.dbContext == undefined) {
                    var sError = "Unable to determine dbContext for new ProductConditionType.";
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);

                    return self;
                }

                // Create record using dbContext
                let newProductConditionTypeRow: WB_ProductConditionTypeRow = <WB_ProductConditionTypeRow>await self.dbContext.createRecordWithKey(esWB_Product_Condition_Type)
                newProductConditionTypeRow.setDefaults()
                self.drWB_ProductConditionType = newProductConditionTypeRow;
                self.dbContext.add(esWB_Product_Condition_Type, newProductConditionTypeRow)
                self.Identity.setAll(self.WB_ProductConditionType.cif.PRD_CNDM_ID, self.WB_ProductConditionType.cif.CONDITION_TYPE, self.WB_ProductConditionType.cif.CONDITION_TYPE_NAME);
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(self.WB_ProductConditionType, 'PRD_CNDM_ID', 'CONDITION_TYPE', 'CONDITION_TYPE_NAME')

                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;
                self._OnLoadComplete = undefined;

                await self.applyCreateOptions()
                self.Identity.setAll(self.WB_ProductConditionType.cif.PRD_CNDM_ID, self.WB_ProductConditionType.cif.CONDITION_TYPE, self.WB_ProductConditionType.cif.CONDITION_TYPE_NAME);
                self.setupEntityChangeBehavior()

                self.LoadProgressProvider.finish();
            } catch (err) {
                self.LoadProgressProvider.exception(err)
                return self
            }

        })();

        return internalOnComplete
    }

    async applyCreateOptions() {
        const self = this

        let sId = self.CreateOptions?.sId || self.LoadOptions?.sId
        if (!_.isEmpty(sId))
            self.WB_ProductConditionType.cif.CONDITION_TYPE = sId

        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName
        if (!_.isEmpty(sName))
            self.WB_ProductConditionType.cif.CONDITION_TYPE_NAME = sName

        //Set Key Set
        let sKeySetId = self.CreateOptions?.sKeySetId || self.LoadOptions?.sKeySetId
        if (!_.isEmpty(sKeySetId))
            self.WB_ProductConditionType.cif.ENTERPRISE_SET_ID = sKeySetId


        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {

                self.WB_ProductConditionType.WB_MasterDataSetRow = mds
                self.WB_ProductConditionType.cif.WB_MDSET_ID = wbMasterDataSet
            }
        }

        // Set Source
        let wbSource = self.CreateOptions?.wbSourceId || self.LoadOptions?.wbSourceId
        if (_.isNumber(wbSource)) {
            let src: WB_SourceRow = <any>await self.dbContext.find(esWB_Source, wbSource)
            if (src) {
                self.WB_ProductConditionType.cif.SOURCE_ID = src.cif.SOURCE_ID

            }
        }

    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;
        if(!this.bIsLoaded)
            this.Identity.setAll( anId, anId,'Loading ProductConditionType (' + anId + ')')

        this._OnLoadComplete = new Promise<IBusinessObject>( (resolve, reject) => {

            self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

            if(self.bIsLoaded) {
                var sError = "ProductConditionType already loaded.";
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.LoadOptions = options || {};
            if('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if(this.dbContext == undefined) {
                var sError = "Unable to determine dbContext for new ProductConditionType.";
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            //Added for record resolver
            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = ProductConditionTypeBO.createRecordResolver({
                    dbContext: self.dbContext
                });
                recordResolverLoadPromise = ProductConditionTypeBO.setupRecordResolver(
                    recordResolver,
                    [anId]
                );
            }

            recordResolverLoadPromise
                .then(function() {
                    let aProductConditionTypeRow: any = recordResolver.get(
                        esWB_Product_Condition_Type,
                        anId
                    );

                    if (_.isEmpty(aProductConditionTypeRow)) {
                        reject("Product Condition Type not Found");
                        return;
                    }

                    self.drWB_ProductConditionType = aProductConditionTypeRow;
                    self.Identity.setAll(self.WB_ProductConditionType.cif.PRD_CNDM_ID, self.WB_ProductConditionType.cif.CONDITION_TYPE, self.WB_ProductConditionType.cif.CONDITION_TYPE_NAME);
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_ProductConditionType,
                        "PRD_CNDM_ID",
                        "CONDITION_TYPE",
                        "CONDITION_TYPE_NAME"
                    );

                    self.setupEntityChangeBehavior()
                    self._bIsLoaded = true;
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch(function(err: any) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                    return;
                });
        });
        return this._OnLoadComplete;
    }

    setupEntityChangeBehavior(): void {
        const self = this
        let qsBeforeSave = new ExecutionSerializer(self, self.BeforeSave, [], 500)
        self.drWB_ProductConditionType.setupOnChangeEvents(['WB_ENTITY_ID_CUSTOMER', 'WB_ENTITY_ID_PRODUCT', 'WB_ENTITY_ID_SUPPLY'], () => {
            qsBeforeSave.Run({})
        })
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    async Validate(options?: any): Promise<IBusinessObject> {

        var self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_ProductConditionType.cif.CONDITION_TYPE_NAME + "(" + self.WB_ProductConditionType.cif.CONDITION_TYPE + ")");
        try {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if(self.dbContext == undefined) {
                var sError = "Unable to determine dbContext for to Validate ProductConditionType.";
                self.ValidateProgressProvider.exception(sError);
                throw sError;
            }

            await ProductConditionTypeBOValidations.ValidateFields(self)
            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false;
            } else {
                self._bIsValidated = true;
            }
            self.ValidateProgressProvider.finish();

        }catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);

        }
        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async BeforeSave(options?: any): Promise<void> {
        const self = this;

        // Before Save

        function logAndThrow(bError: boolean, sError: string){
            if (bError) {
                self._bIsSaved = false;
                self.SaveProgressProvider.exception(sError);
                throw new Error(sError)
            }
        }

        logAndThrow(_.isEmpty(this.dbContext), "Unable to determine dbContext to Save ProductConditionType.")
        if (self.drWB_ProductConditionType.cif.WB_ENTITY_ID_CUSTOMER == 0) {
            self.drWB_ProductConditionType.cif.CUST_POLICY = "0"
            self.drWB_ProductConditionType.cif.CUSTOMER_TYPE = ""
        } else {
            let ce = <WB_EntityRow> await self.dbContext.find(esWB_Entity, self.drWB_ProductConditionType.cif.WB_ENTITY_ID_CUSTOMER)
            logAndThrow(_.isEmpty(ce), "Unable to determine Customer Entity Type")
            self.drWB_ProductConditionType.cif.CUST_POLICY = "1"
            self.drWB_ProductConditionType.cif.CUSTOMER_TYPE = ce.cif.ENTITY_ID
        }

        if (self.drWB_ProductConditionType.cif.WB_ENTITY_ID_PRODUCT == 0) {
            self.drWB_ProductConditionType.cif.PRD_POLICY = "0"
            self.drWB_ProductConditionType.cif.PRDGRP_POLICY = "0"
            self.drWB_ProductConditionType.cif.PRDGRP_LEVEL_ID = ""
        } else {
            let pe = <WB_EntityRow> await self.dbContext.find(esWB_Entity, self.drWB_ProductConditionType.cif.WB_ENTITY_ID_PRODUCT)
            logAndThrow(_.isEmpty(pe), "Unable to determine Product Entity Type")
            switch (pe.cif.ENTITY_ID){
                case "FinishedProduct":
                case "ConsumerProduct":
                    self.drWB_ProductConditionType.cif.PRD_POLICY = "1"
                    self.drWB_ProductConditionType.cif.PRDGRP_POLICY = "0"
                    self.drWB_ProductConditionType.cif.PRDGRP_LEVEL_ID = ""
                    break;
                default:
                    self.drWB_ProductConditionType.cif.PRD_POLICY = "0"
                    self.drWB_ProductConditionType.cif.PRDGRP_POLICY = "1"
                    self.drWB_ProductConditionType.cif.PRDGRP_LEVEL_ID = pe.cif.ENTITY_ID
                    break;

            }
        }

    }

    async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this;
        const wbProductConditionType = <IwbProductConditionType>self.drWB_ProductConditionType;
        try {
            self.SaveProgressProvider.start("Saving " + wbProductConditionType.CONDITION_TYPE_NAME + "(" + wbProductConditionType.CONDITION_TYPE + ")");
            let auditService: AuditService = undefined


            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.


            self.SaveOptions = options || {};

            await self.BeforeSave(options)

            // Save
            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            await self.Validate({})

            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return self;
            }

            auditService = await ProductConditionTypeBOAudit.ProductConditionTypeAuditBO(self)


            await saveFunction(esWB_Product_Condition_Type, self.drWB_ProductConditionType, {})


            if (auditService)
                await auditService.Save(bBatch)

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                })
            } else {
                self._bIsSaved = true;
            }
            self.SaveProgressProvider.finish();
            return self;

        } catch (err) {
            self.SaveProgressProvider.exception(err);
            return self
        }
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }


//Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbProductConditionType = <IwbProductConditionType>self.drWB_ProductConditionType;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbProductConditionType.CONDITION_TYPE + "(" + wbProductConditionType.PRD_CNDM_ID + ")");
        return new Promise<IBusinessObject>( (resolve, error) => {

            // 1. Validate if we can delete the ProductConditionType

            // 2. If Valid, mark all the records for deletion.
            self.drWB_ProductConditionType.markDeleted()

            if(bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>

    //<editor-fold desc="IHaveRecordResolver -- static">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const productConditionType_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, productConditionType_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver
                .findAllWithRelationships(esWB_Product_Condition_Type, ids)
                .then(result => {
                    resolve();
                })
                .catch(err => {
                    error(err);
                });
        });
    }

    //</editor-fold>

};

PAEnvironment.registerBusinessObjectClass(ProductConditionTypeBO, "ProductConditionTypeBO", "Product Condition Type")